import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { APP_CONFIG } from './config';
import App from './App.tsx';
// Import directo, no por el barrel de `ui`: el barrel arrastraría todo el
// design system al chunk de entrada.
import { AppErrorFallback } from '@shared/components/ui/AppErrorFallback';
import { registerChunkPreloadRecovery } from '@shared/utils/chunkReload';
import './index.css';
import './shared/i18n/i18n';

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const shouldEnableSentryDebugMode = import.meta.env.VITE_SENTRY_DEBUG === 'true';
const appEnvironment = import.meta.env.VITE_APP_ENV || import.meta.env.MODE;

const sentryAppErrorFallback: Sentry.FallbackRender = ({ resetError }) => (
  <AppErrorFallback variant="fullScreen" onRetry={() => resetError()} data-testid="sentry-root-error-fallback" />
);

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: appEnvironment,
    integrations: [
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.browserProfilingIntegration(),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    profilesSampleRate: 1.0,
    enableLogs: true,
    enableMetrics: true,
    enabled: import.meta.env.PROD || shouldEnableSentryDebugMode,
    beforeSend(event) {
      if (typeof window === 'undefined') {
        return event;
      }
      const { pathname, search, hash, href } = window.location;
      event.tags = { ...event.tags, pathname };
      event.contexts = {
        ...event.contexts,
        route: {
          pathname,
          search,
          hash,
          href,
        },
      };
      return event;
    },
  });

  // En modo debug enviamos un smoke test para validar la recepción de eventos.
  if (shouldEnableSentryDebugMode) {
    const smokeTestId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const smokeMessage = `[smoke:${smokeTestId}] [env:${appEnvironment}] Smoke test Beeping`;

    try {
      Sentry.captureException(new Error(smokeMessage));
    } catch {
      //
    }
  }
}

// Establecer título inicial desde la configuración
document.title = APP_CONFIG.appName;

// Un despliegue retira los chunks de la versión anterior: los `import()` que
// fallen fuera de una frontera de ruta se recuperan recargando.
registerChunkPreloadRecovery();

const appTree = (
  <StrictMode>
    <App />
  </StrictMode>
);

createRoot(document.getElementById('root')!).render(
  sentryDsn ? (
    <Sentry.ErrorBoundary
      fallback={sentryAppErrorFallback}
      beforeCapture={(scope) => {
        scope.setTag('error_boundary', 'root');
      }}
    >
      {appTree}
    </Sentry.ErrorBoundary>
  ) : (
    appTree
  ),
);
